import { createSelector } from "@ngrx/store"
import { environment } from "../../../environments/environment"
import { ResourcesTableData } from "../../helpers/table-column-types"
import { LabelMaps, LabelObject, Ranking } from "globals/store/events.data"
import { BriefInfo, PublicEditProfileInfo, shareUrlsByType } from "globals/store/share.data"
import { WFWorker } from "globals/store/workforce.data"
import { getActiveLights, getBooleanConfirmedLight } from "./performerSponsors.reselector"
import { filterResources, JoiResource } from 'globals/store/resources.data';
import { defaultColumns, settingsStateSelector } from "../settings.era"
import { resourceColumnsById } from "../../helpers/table-helpers"
import { getResourceBriefActions } from "../share.brief.functions";
import { getMediaTermsLights } from "./entity.reselector.helpers";
import { eventStateSelector, JoiEvent } from 'src/app/store/events.era';
import { EntitySubTable, resourceSubTableTypes, subTableIds } from "globals/store/entity.data";
import { TableColumn } from "../../helpers/table.data";
import { getBaseResourceTableData, getReadyAssetLights, resourceListReselector } from "./entity.decircular.reselector"
import { Dictionary } from "@ngrx/entity"
import { Asset } from "globals/store/assets.data"
import { allAssociatedAssetsIdsReselector } from "./entity.reselector"
import { workforceStateSelector } from "../workforce.era"
import { filterStateSelector } from "../filters.era"
import { shareStateSelector } from "../share.era"
import { budgetStateSelector } from "../budget.era"
import { resourcesStateSelector } from "../resources.era"
import { assetsStateSelector } from "../assets.era"
import { makeMapFromListOfObjectsAndKey } from "globals/helpers/helpers"

const workersByEmail = createSelector(workforceStateSelector, (state) => state.workersByEmail)
const labelMaps = createSelector(eventStateSelector, (state) => state.labelMaps)
const resourceTableFilters = createSelector(filterStateSelector, (state) => state.resources)
const resourceProfileEditShareables = createSelector(shareStateSelector, (state) => state.resource)
const resourceBriefs = createSelector(shareStateSelector, (state) => state.resourceBrief)
const entityAssetShareables = createSelector(shareStateSelector, (state) => state.entityAsset)
const userSettings = createSelector(settingsStateSelector, (state) => state.user)
const event = createSelector(eventStateSelector, (state) => state.event)
const budget = createSelector(budgetStateSelector, (state) => state.budget)
const quoteShareables = createSelector(shareStateSelector, (state) => state.quote)
const resourceTypes = createSelector(resourcesStateSelector, (state) => state.resourceTypes)
const assets = createSelector(assetsStateSelector, (state) => state.entities)

const SearchableResourceFields = ['name']

/* 
export const quotesReselector = createSelector(
)
 */

export const resourceColumnsReselector = createSelector(
  userSettings,
  (settings) => {
    const tableColumnsMap: { [t in EntitySubTable]: TableColumn[] } = {} as { [t in EntitySubTable]: TableColumn[] }
    if (!event) return tableColumnsMap
    subTableIds.forEach(id => {
      if (id === 'responses' || id === 'publish') return
      const selectedColumns = !settings ? null : settings[`${resourceSubTableTypes[id]}_columns`]
      const defaults = defaultColumns[resourceSubTableTypes[id]]
      const columns = selectedColumns || defaults
      if (columns) tableColumnsMap[id] = columns.filter(c => c.selected).map(c => resourceColumnsById[c.value]).filter((c: TableColumn) => !c.notOuter)
    })
    return tableColumnsMap
  }
)

export const resourceBriefActionsReselector = createSelector(
  resourceProfileEditShareables,
  event,
  budget,
  quoteShareables,
  entityAssetShareables,
  getResourceBriefActions
)

export const resourceTablePreselector = createSelector( // a selector can only have 10 inputs at most
  assets,
  allAssociatedAssetsIdsReselector,
  workersByEmail,
  labelMaps, 
  (assets, associatedAssetIdsByEntityId, workersByEmail, labelMaps ) => 
    ({ assets, associatedAssetIdsByEntityId, workersByEmail, labelMaps })
)

export const resourcesTableReselector = createSelector(
  resourceTablePreselector,
  resourceListReselector,
  resourceTypes,
  resourceTableFilters,
  resourceProfileEditShareables,
  event,
  resourceBriefs,
  ( { assets, associatedAssetIdsByEntityId, workersByEmail, labelMaps },
    resourceList: JoiResource[], 
    resourceTypes,
    resourceTableFilters,
    resourceShareables,
    event,
    resourceBriefs
  ) => {
    const { search } = resourceTableFilters
    if (search) {
      const searchTerm = search.toLocaleLowerCase()
      resourceList = resourceList.filter(p => SearchableResourceFields.some(field => p[field] && p[field].toLocaleLowerCase().includes(searchTerm)))
    }
    resourceList = filterResources(resourceTableFilters, resourceList)
    const resourceTableData: ResourcesTableData[] = resourceList.map(resource => 
      getResourceTableData(resource, workersByEmail, labelMaps.resource_labels, resourceShareables[resource.id], resourceTypes, event, resourceBriefs[resource.id], associatedAssetIdsByEntityId, assets)
    )
    const resourceTableRowsById = makeMapFromListOfObjectsAndKey(resourceTableData, 'id')
    return { resourceTableData, resourceTableRowsById }
  }
)


const getResourceTableData = (
  resource: JoiResource, 
  workersByEmail: { [key: string]: WFWorker }, 
  resourceLabels: LabelObject, 
  shareable: PublicEditProfileInfo,
  resourceTypes: { [key: string]: Ranking },
  event: JoiEvent, 
  briefInfo: BriefInfo,
  associatedAssetIdByEntityId: Dictionary<string[]>, 
  assets: Dictionary<Asset>
  ): ResourcesTableData => {

  const { workforceContactEmail, confirmed, labels, files, id, entityTypeId } = resource
  const contact = workersByEmail[workforceContactEmail]
  const resourceType = !entityTypeId || !resourceTypes[entityTypeId] ? null : resourceTypes[entityTypeId]

  const resourceRow: ResourcesTableData = {
    ...getBaseResourceTableData(resource),
    detailsConfirmed: getBooleanConfirmedLight(confirmed),
    workforceContact: !contact ? null : contact,
    editingUrlStatus: getActiveLights(!shareable ? null : shareable.status, !shareable ? null : environment.joi.shareBase + shareUrlsByType.sponsor + '/' + shareable.code ),
    labels: labels ? labels.filter(label => resourceLabels[label]).map(label => ({ color: resourceLabels[label].color, label: resourceLabels[label].label })) : [],
    files: { text: !files ? '' : files.length + '' },
    assets: getReadyAssetLights(associatedAssetIdByEntityId[id], assets),
    resourceType: { rank: !resourceType ? 0 : resourceType.rank, text: !resourceType ? '' : resourceType.name },
    terms: !event?.mediaTerms?.resource ? null : getMediaTermsLights(event.mediaTerms.resource, resource, briefInfo)
  }
  return resourceRow
}
